import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { VersionsData } from '@/src/services/getVersionsData';
import Icon from '../common/Icon';
import PreviousVersion from './parts/PreviousVersion';

const PreviousVersionsComponent = ({ versionsData }: { versionsData: VersionsData }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const [searchValue, setSearchValue] = useState('');
  const [slice, setSlice] = useState(4);

  const current = versionsData.current;
  const allVersions = versionsData.previous;

  const handleSearch = (e) => {
    setSearchValue(e.target.value);
  };

  const clearSearch = () => {
    setSearchValue('');
  };

  const loadMore = () => {
    if (slice < allVersions.length) {
      setSlice((prevSlice) => prevSlice + 4);
    }
  };

  const matchesSearch = (el: { version: string; date: string }) =>
    el.version.toLowerCase().includes(searchValue.toLowerCase()) ||
    el.date.toLowerCase().includes(searchValue.toLowerCase());

  const showCurrent = current && (searchValue ? matchesSearch(current) : true);

  const terminalsFiltered = useMemo(() => {
    if (searchValue) {
      return allVersions.filter(matchesSearch);
    }
    return allVersions.slice(0, slice);
  }, [slice, searchValue, allVersions]);

  return (
    <div className="content previous-versions">
      <div className="left">
        <Link passHref href="/terminal/download">
          <div className="back-link">
            <Icon name="20-darr" file="sprite" width="12" height="12" />
            {t('back')}
          </div>
        </Link>
        <h1 className="title">{t('previous-versions_title')}</h1>
        <p className="subtitle">{t('previous-versions_subtitle')}</p>
        <div
          className="agreement"
          dangerouslySetInnerHTML={{ __html: t('previous-versions_agreement') }}
        />
        <div className="input">
          <Icon file="sprite" name="21-search" width="21" height="24" />
          <input
            type="text"
            placeholder={t('previous-versions_search-placeholder')}
            onChange={handleSearch}
            value={searchValue}
          />
          {searchValue ? (
            <button className="clear-btn" type="button" onClick={clearSearch}>
              <Icon file="sprite" name="16-close" width="20" height="20" />
            </button>
          ) : null}
          <p className="found">
            {t('previous-versions_search-results')}{' '}
            {(showCurrent ? 1 : 0) + terminalsFiltered.length}
          </p>
        </div>
        <>
          {showCurrent && (
            <PreviousVersion
              key={`current-${current.version}`}
              name={`Tiger.Trade ${current.version}`}
              date={current.date}
              moreLink={current.support || t('download_platform-updates-link')}
              downloadLink={current.downloadUrl}
              supText="Recommended"
            />
          )}
          {terminalsFiltered.map((entry) => (
            <PreviousVersion
              key={`${entry.version}-${entry.build}`}
              name={`Tiger.Trade ${entry.version}`}
              date={entry.date}
              moreLink={entry.support || t('download_platform-updates-link')}
              downloadLink={entry.downloadUrl}
              supText={entry.notes || undefined}
            />
          ))}
        </>
        {searchValue || slice >= allVersions.length ? null : (
          <button className="load-more" type="button" onClick={loadMore}>
            {t('previous-versions_load-more')}
          </button>
        )}
      </div>
      <div className="right">
        <div className="col wide">
          <div className="info-title">{t('download_system_title')}</div>
          <p>{t('download_system_os1')}</p>
          <p>{t('download_system_requirements')}</p>
        </div>
        <div className="col wide latest">
          <div className="info-title">{t('previous_versions_latest-title')}</div>
          {current ? (
            <>
              <p>
                Tiger.Trade {current.version} ({current.date})
              </p>
              <a
                href={current.downloadUrl}
                onClick={(e) => {
                  e.preventDefault();
                  window.open(current.downloadUrl, '_blank');
                  router.push('/terminal/how-to-setup');
                }}
              >
                {t('previous_versions_download-text')}
              </a>
            </>
          ) : (
            <>
              <p>
                {t('download_version')} ({t('download_version_date')})
              </p>
              <Link href={t('previous_versions_download-link')}>
                {t('previous_versions_download-text')}
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PreviousVersionsComponent;
